import * as THREE from 'three';
import { TABLE, type TableSpec, type Theme } from '../config';

/** Floor level of the room (the table stands on legs above it). */
export const FLOOR_Z = -0.75;

/**
 * A dim "billiard hall" surround: a floor plane and four table legs so the
 * table sits in a room instead of floating. Pair with {@link makeBackdrop} for
 * the far backdrop. Returns a group to add to the scene.
 */
export function buildHall(theme: Theme, table: TableSpec = TABLE): THREE.Group {
  const group = new THREE.Group();

  // floor — slightly reflective dark wood so the overhead lamps pool on it
  const floorMat = new THREE.MeshStandardMaterial({ color: theme.floor, roughness: 0.6, metalness: 0.1 });
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(16, 16), floorMat);
  floor.position.z = FLOOR_Z;
  floor.receiveShadow = true;
  group.add(floor);

  // four legs from the underside of the table down to the floor
  const tableBottom = -0.012 - table.bedThickness;
  const legH = tableBottom - FLOOR_Z;
  const legMat = new THREE.MeshStandardMaterial({ color: theme.railWood, roughness: 0.5, metalness: 0.05 });
  const legGeo = new THREE.BoxGeometry(0.09, 0.09, legH);
  const lx = table.playWidth / 2 + table.railWidth;
  const ly = table.playLength / 2 + table.railWidth;
  for (const sx of [-1, 1]) {
    for (const sy of [-1, 1]) {
      const leg = new THREE.Mesh(legGeo, legMat);
      leg.position.set(sx * lx, sy * ly, FLOOR_Z + legH / 2);
      leg.castShadow = true;
      group.add(leg);
    }
  }

  return group;
}

/** Radial gradient backdrop: a faint pool of light at the table fading to a dim room. */
export function makeBackdrop(theme: Theme): THREE.Texture {
  const size = 512;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d')!;
  const base = new THREE.Color(theme.backdrop);
  const lit = base.clone().lerp(new THREE.Color(0xffffff), 0.12);
  const grad = ctx.createRadialGradient(size / 2, size * 0.42, size * 0.05, size / 2, size * 0.5, size * 0.7);
  grad.addColorStop(0, '#' + lit.getHexString());
  grad.addColorStop(1, '#' + base.getHexString());
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
